import { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  changeMatchState,
  changeUserMode,
} from "../redux/slices/userModeSlice";
import { setArray } from "../redux/slices/finishedTilesArraySlice";
import { setBalance } from "../redux/slices/userSlice";
import {
  resetGameDataWithoutMultiplier,
  resetOpenedIndexes,
  setWinningAmount,
  resetGameData,
  setCurrentMultiplier,
  addToWinningCounter,
  addToLosingCounter,
} from "../redux/slices/gameDataSlice";
import {
  changeBetAmount,
  changeAutoBetsCurrentNumberOfRounds,
} from "../redux/slices/gameSettingsSlice";
import {
  lostGame,
  beforePlay,
  inPlay,
  wonGame,
  duringAutoPlay,
} from "../redux/types/userModeTypes";
import { arrayLength } from "../config";
import alienSound from "../audios/alien.mp3";
import cashoutMusic from "../audios/cashout.mp3";
import useSound from "use-sound";
import getSoundEffectVolume from "./getSoundEffectVolume";
import autoApi from "../api/autoApi";

const useAuto = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const gameSettings = useSelector((state) => state.gameSettings);
  const userMode = useSelector((state) => state.userMode);
  const sounds = useSelector((state) => state.sounds); // Get sound state
  // Create refs to store sound effects and mute state
  const [playAlienSound] = useSound(alienSound, {
    volume: getSoundEffectVolume(),
    soundEnabled: !sounds.isSoundEffectsMute,
  });
  const [playCashoutSound] = useSound(cashoutMusic, {
    volume: getSoundEffectVolume(),
    soundEnabled: !sounds.isSoundEffectsMute,
  });

  const userModeRef = useRef(userMode);
  const soundEffectsMuteRef = useRef(sounds.isSoundEffectsMute);

  useEffect(() => {
    userModeRef.current = userMode;
  }, [userMode]);

  useEffect(() => {
    soundEffectsMuteRef.current = sounds.isSoundEffectsMute;
  }, [sounds.isSoundEffectsMute]);

  const auto = async () => {
    var betAmount = gameSettings.betAmount;
    let counter = gameSettings.autoBetsNumberOfRounds;
    const defaultBetAmount = gameSettings.betAmount;
    while (
      counter > 0 &&
      !gameSettings.isInManualMode &&
      (userModeRef.current.userModeState === duringAutoPlay ||
        counter === gameSettings.autoBetsNumberOfRounds)
    ) {
      dispatch(changeAutoBetsCurrentNumberOfRounds(counter));
      if (betAmount > user.balance) {
        break;
      }
      const res = await autoApi(
        betAmount,
        gameSettings.minesAmount,
        user.uid,
        gameSettings.selectedTiles
      );

      if (res !== null) {
        if (res.currentMultiplier === undefined) {
          res.currentMultiplier = 0;
        }

        if (res.result === "won") {
          //--------win-------
          let myArray = new Array(arrayLength);
          myArray.fill("win");
          res.minesIndexes.forEach((i) => {
            myArray[i] = "lose";
          });

          if (!soundEffectsMuteRef.current) {
            playCashoutSound();
          }
          dispatch(addToWinningCounter());
          dispatch(setArray(myArray));
          dispatch(changeMatchState(wonGame));
          dispatch(resetOpenedIndexes());

          if (user.balance !== res.currentBalance) {
            dispatch(setBalance(res.currentBalance));
          }
          if (gameSettings.onWinStopValue > 0) {
            if (res.currentBalance >= gameSettings.onWinStopValue) {
              break;
            }
          }
          if (gameSettings.isOnWinReset) {
            //reset
            if (defaultBetAmount <= res.currentBalance) {
              betAmount = defaultBetAmount;
              dispatch(changeBetAmount(defaultBetAmount));
            }
          } else {
            const newBetAmount =
              (betAmount * (parseInt(gameSettings.onWinPercentage) + 100)) /
              100;

            if (newBetAmount > 1 && newBetAmount <= res.currentBalance) {
              betAmount = newBetAmount;
              dispatch(changeBetAmount(newBetAmount));
            }
          }
        } else {
          //--------loss-------

          if (!soundEffectsMuteRef.current) {
            playAlienSound();
          }

          let myArray = new Array(arrayLength);
          myArray.fill("win");
          if (res.minesIndexes) {
            res.minesIndexes.forEach((i) => {
              myArray[i] = "lose";
            });
          }
          dispatch(addToLosingCounter());

          dispatch(setArray(myArray));
          dispatch(changeMatchState(lostGame));

          if (user.balance !== res.currentBalance) {
            dispatch(setBalance(res.currentBalance));
          }

          if (gameSettings.betAmount > user.balance) {
            dispatch(changeBetAmount(user.balance));
          }

          if (res.currentBalance <= gameSettings.onLossStopValue) {
            break;
          }

          if (gameSettings.isOnLossReset) {
            //reset
            if (defaultBetAmount <= res.currentBalance) {
              betAmount = defaultBetAmount;
              dispatch(changeBetAmount(defaultBetAmount));
            }
          } else {
            const newBetAmount =
              (betAmount * (parseInt(gameSettings.onLossPercentage) + 100)) /
              100;

            if (newBetAmount > 1 && newBetAmount <= res.currentBalance) {
              betAmount = newBetAmount;
              dispatch(changeBetAmount(newBetAmount));
            }
          }
        }
        if (res.winningAmount === undefined) {
          dispatch(setWinningAmount(0));
        } else {
          dispatch(setWinningAmount(res.winningAmount));
        }
        if (res.currentMultiplier === undefined) {
          dispatch(setCurrentMultiplier(0));
        } else {
          dispatch(setCurrentMultiplier(res.currentMultiplier));
        }

        if (betAmount > res.currentBalance) {
          break;
        }
      }

      await new Promise((resolve) =>
        setTimeout(() => {
          dispatch(resetGameDataWithoutMultiplier());
          dispatch(changeMatchState(inPlay));
          resolve();
        }, 1100)
      );
      await new Promise((resolve) => setTimeout(resolve, 30));
      counter--;
    }
    console.log("reset");
    dispatch(resetGameDataWithoutMultiplier());
    dispatch(changeMatchState(inPlay));
    dispatch(resetGameData());
    dispatch(changeUserMode(beforePlay));
  };

  return auto;
};

export default useAuto;
